import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import { Toaster, toast } from 'sonner';
import axios from 'axios';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft,
  X,
  Plus,
  ImagePlus,
  Video,
  Loader2,
  Save,
  CalendarDays,
  Clock,
  Repeat,
  Trash2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const cuisineTypes = [
  'Italian', 'Japanese', 'Mexican', 'French', 'Indian', 'Thai', 'Chinese',
  'Mediterranean', 'American', 'Korean', 'Vietnamese', 'Greek', 'Spanish',
  'Middle Eastern', 'Ethiopian', 'Peruvian', 'Caribbean', 'Fusion'
];

const categories = [
  'Home Cooked', 'Food Tour', 'Aperitivo', 'Supper Club',
  'Farm to Table', 'Cooking Class', 'Street Food', 'BBQ & Grill',
  'Sweet Tooth', 'Private Chef'
];

const mealTypes = ['Breakfast', 'Brunch', 'Lunch', 'Dinner', 'Late Night', 'All Day'];

const amenitiesList = [
  'Vegetarian Options', 'Vegan Options', 'Gluten-Free', 'Halal', 'Kosher',
  'Kids Menu', 'Wine Included', 'BYOB Allowed', 'Outdoor Seating', 'Private Room',
  'Wheelchair Accessible', 'Parking Available', 'Cooking Demo', 'Recipe Card Included',
  'Take-Home Treats', 'Chef Interaction', 'Photo Opportunities'
];

const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const timeSlots = [
  '06:00', '06:30', '07:00', '07:30', '08:00', '08:30', '09:00', '09:30',
  '10:00', '10:30', '11:00', '11:30', '12:00', '12:30', '13:00', '13:30',
  '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
  '18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00', '21:30',
  '22:00', '22:30', '23:00'
];

const CreateEditExperiencePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const isEditing = !!id;
  
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditing);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [availabilityTab, setAvailabilityTab] = useState('specific'); // 'specific' or 'recurring'
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    cuisine: '',
    category: '',
    meal_type: '',
    price: '',
    max_guests: 8,
    duration: '2 hours',
    amenities: [],
    images: [],
    video: null,
    is_active: true,
    availability: {
      specific_dates: [],
      recurring: null
    }
  });

  // For adding specific dates
  const [selectedDate, setSelectedDate] = useState(null);
  const [specificStartTime, setSpecificStartTime] = useState('18:00');
  const [specificEndTime, setSpecificEndTime] = useState('21:00');
  
  // For recurring schedule
  const [recurringDays, setRecurringDays] = useState([]);
  const [recurringStartTime, setRecurringStartTime] = useState('18:00');
  const [recurringEndTime, setRecurringEndTime] = useState('21:00');

  useEffect(() => {
    if (isEditing) {
      fetchExperience();
    }
  }, [id]);

  const fetchExperience = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/experiences/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const exp = response.data;
      
      // Parse availability - handle both old and new formats
      let availability = { specific_dates: [], recurring: null };
      if (exp.availability) {
        // If it has the new format with type/days/time_slots
        if (exp.availability.type === 'weekly' && exp.availability.days) {
          availability = {
            specific_dates: exp.availability.specific_dates || [],
            recurring: {
              days: exp.availability.days || [],
              start_time: exp.availability.time_slots?.[0] || '18:00',
              end_time: exp.availability.time_slots?.[1] || '21:00',
            }
          };
        } else {
          // Old format with specific_dates and recurring
          availability = {
            specific_dates: exp.availability.specific_dates || [],
            recurring: exp.availability.recurring || null
          };
        }
      }
      
      setFormData({
        title: exp.title || '',
        description: exp.description || '',
        location: exp.location || '',
        cuisine: exp.cuisine || '',
        category: exp.category || '',
        meal_type: exp.meal_type || '',
        price: exp.price?.toString() || '',
        max_guests: exp.max_guests || 8,
        duration: exp.duration || '2 hours',
        amenities: Array.isArray(exp.amenities) ? exp.amenities : [],
        images: Array.isArray(exp.images) ? exp.images : [],
        video: exp.video || null,
        is_active: exp.is_active ?? true,
        availability: availability
      });
      
      // Set recurring schedule if exists
      if (availability.recurring) {
        setRecurringDays(availability.recurring.days || []);
        setRecurringStartTime(availability.recurring.start_time || '18:00');
        setRecurringEndTime(availability.recurring.end_time || '21:00');
        setAvailabilityTab('recurring');
      }
    } catch (error) {
      console.error('Failed to load experience:', error);
      toast.error('Failed to load experience');
      navigate('/dashboard');
    } finally {
      setFetching(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleAmenity = (amenity) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  // Availability functions
  const addSpecificDate = () => {
    if (!selectedDate) {
      toast.error('Please select a date');
      return;
    }
    
    const dateStr = selectedDate.toISOString().split('T')[0];
    
    // Check if date already exists
    const specificDates = formData.availability?.specific_dates || [];
    if (specificDates.some(d => d.date === dateStr)) {
      toast.error('This date is already added');
      return;
    }
    
    if (specificStartTime >= specificEndTime) {
      toast.error('End time must be after start time');
      return;
    }
    
    const newSlot = {
      date: dateStr,
      start_time: specificStartTime,
      end_time: specificEndTime
    };
    
    setFormData(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        specific_dates: [...prev.availability.specific_dates, newSlot].sort((a, b) => 
          new Date(a.date) - new Date(b.date)
        )
      }
    }));
    
    setSelectedDate(null);
    toast.success('Date added');
  };

  const removeSpecificDate = (dateToRemove) => {
    setFormData(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        specific_dates: prev.availability.specific_dates.filter(d => d.date !== dateToRemove)
      }
    }));
  };

  const toggleRecurringDay = (day) => {
    setRecurringDays(prev => 
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const saveRecurringSchedule = () => {
    if (recurringDays.length === 0) {
      toast.error('Please select at least one day');
      return;
    }
    
    if (recurringStartTime >= recurringEndTime) {
      toast.error('End time must be after start time');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        recurring: {
          days: recurringDays,
          start_time: recurringStartTime,
          end_time: recurringEndTime
        }
      }
    }));
    
    toast.success('Recurring schedule saved');
  };

  const clearRecurringSchedule = () => {
    setRecurringDays([]);
    setFormData(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        recurring: null
      }
    }));
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  }, [formData.images.length, formData.video]);

  const handleFiles = async (files) => {
    for (const file of Array.from(files)) {
      const isVideo = file.type.startsWith('video/');
      const isImage = file.type.startsWith('image/');

      if (!isVideo && !isImage) {
        toast.error(`${file.name} is not a supported file type`);
        continue;
      }

      if (isVideo) {
        if (formData.video) {
          toast.error('Only 1 video allowed. Remove existing video first.');
          continue;
        }
        if (file.size > 200 * 1024 * 1024) {
          toast.error('Video must be under 200MB');
          continue;
        }
        await uploadVideoChunked(file);
      }

      if (isImage) {
        if (formData.images.length >= 5) {
          toast.error('Maximum 5 images allowed');
          continue;
        }
        await uploadFile(file, 'image');
      }
    }
  };

  const uploadVideoChunked = async (file) => {
    setUploadingMedia(true);
    setUploadProgress(0);
    
    const CHUNK_SIZE = 1024 * 1024;
    
    try {
      const initResponse = await axios.post(`${API_URL}/api/upload/init`, {
        filename: file.name,
        total_size: file.size,
        content_type: file.type
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { upload_id } = initResponse.data;
      
      let offset = 0;
      while (offset < file.size) {
        const chunk = file.slice(offset, offset + CHUNK_SIZE);
        const chunkFormData = new FormData();
        chunkFormData.append('file', chunk);
        
        const chunkResponse = await axios.post(
          `${API_URL}/api/upload/chunk/${upload_id}`,
          chunkFormData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        
        setUploadProgress(chunkResponse.data.progress);
        offset += CHUNK_SIZE;
      }
      
      const completeResponse = await axios.post(
        `${API_URL}/api/upload/complete/${upload_id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const fileUrl = `${API_URL}${completeResponse.data.url}`;
      setFormData(prev => ({ ...prev, video: fileUrl }));
      toast.success('Video uploaded successfully!');
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to upload video';
      toast.error(message);
    } finally {
      setUploadingMedia(false);
      setUploadProgress(0);
    }
  };

  const uploadFile = async (file, type) => {
    setUploadingMedia(true);
    setUploadProgress(0);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axios.post(`${API_URL}/api/upload`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      
      const fileUrl = `${API_URL}${response.data.url}`;
      
      if (type === 'video') {
        setFormData(prev => ({ ...prev, video: fileUrl }));
        toast.success('Video uploaded successfully!');
      } else {
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, fileUrl]
        }));
      }
    } catch (error) {
      const message = error.response?.data?.detail || `Failed to upload ${type}`;
      toast.error(message);
    } finally {
      setUploadingMedia(false);
      setUploadProgress(0);
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const removeVideo = () => {
    setFormData(prev => ({ ...prev, video: null }));
  };

  const validateForm = () => {
    if (!formData.title || formData.title.length < 5) {
      toast.error('Title must be at least 5 characters');
      return false;
    }
    if (!formData.description || formData.description.length < 20) {
      toast.error('Description must be at least 20 characters');
      return false;
    }
    if (formData.description.length > 2500) {
      toast.error('Description must be less than 500 words (~2500 characters)');
      return false;
    }
    if (!formData.location) {
      toast.error('Location is required');
      return false;
    }
    if (!formData.cuisine) {
      toast.error('Cuisine type is required');
      return false;
    }
    if (!formData.category) {
      toast.error('Category is required');
      return false;
    }
    if (!formData.meal_type) {
      toast.error('Meal type is required');
      return false;
    }
    if (!formData.price || parseFloat(formData.price) <= 0) {
      toast.error('Valid price is required');
      return false;
    }
    if (formData.images.length < 5) {
      toast.error('At least 5 images are required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        images: formData.images.slice(0, 5)
      };

      if (isEditing) {
        await axios.put(`${API_URL}/api/experiences/${id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Experience updated!');
      } else {
        await axios.post(`${API_URL}/api/experiences`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Experience created!');
      }
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save experience';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const wordCount = formData.description.split(/\s+/).filter(Boolean).length;

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const displayHour = h % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  if (fetching) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D65A31]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="create-edit-page">
      <Toaster />
      
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-xl font-semibold">
              {isEditing ? 'Edit Experience' : 'Create New Experience'}
            </h1>
          </div>
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-[#D65A31] hover:bg-[#B84A28] text-white"
            data-testid="save-btn"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving...</>
            ) : (
              <><Save className="w-4 h-4 mr-2" /> {isEditing ? 'Update' : 'Create'}</>
            )}
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Info */}
          <section className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold mb-6">Basic Information</h2>
            
            <div className="space-y-6">
              <div>
                <Label htmlFor="title">Experience Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="e.g., Authentic Italian Pasta Making with Nonna"
                  className="mt-2"
                  maxLength={100}
                  data-testid="title-input"
                />
                <p className="text-sm text-gray-500 mt-1">{formData.title.length}/100 characters</p>
              </div>

              <div>
                <Label htmlFor="description">Description * (up to 500 words)</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Describe what guests will experience, taste, and learn..."
                  className="mt-2 min-h-[200px]"
                  maxLength={2500}
                  data-testid="description-input"
                />
                <p className="text-sm text-gray-500 mt-1">
                  {wordCount}/500 words · {formData.description.length}/2500 characters
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="location">Location *</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    placeholder="City name"
                    className="mt-2"
                    data-testid="location-input"
                  />
                </div>
                <div>
                  <Label>Cuisine Type *</Label>
                  <Select
                    value={formData.cuisine}
                    onValueChange={(value) => handleInputChange('cuisine', value)}
                  >
                    <SelectTrigger className="mt-2" data-testid="cuisine-select">
                      <SelectValue placeholder="Select cuisine" />
                    </SelectTrigger>
                    <SelectContent>
                      {cuisineTypes.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => handleInputChange('category', value)}
                  >
                    <SelectTrigger className="mt-2" data-testid="category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Meal Type *</Label>
                  <Select
                    value={formData.meal_type}
                    onValueChange={(value) => handleInputChange('meal_type', value)}
                  >
                    <SelectTrigger className="mt-2" data-testid="meal-type-select">
                      <SelectValue placeholder="Select meal type" />
                    </SelectTrigger>
                    <SelectContent>
                      {mealTypes.map((m) => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </section>

          {/* Capacity & Pricing */}
          <section className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold mb-6">Capacity & Pricing</h2>
            
            <div className="grid grid-cols-3 gap-6">
              <div>
                <Label htmlFor="price">Price per person (£) *</Label>
                <Input
                  id="price"
                  type="number"
                  value={formData.price}
                  onChange={(e) => handleInputChange('price', e.target.value)}
                  placeholder="85"
                  className="mt-2"
                  min="1"
                  data-testid="price-input"
                />
              </div>
              <div>
                <Label>Maximum Guests</Label>
                <div className="flex items-center gap-3 mt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleInputChange('max_guests', Math.max(1, formData.max_guests - 1))}
                  >
                    -
                  </Button>
                  <span className="w-12 text-center font-medium">{formData.max_guests}</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleInputChange('max_guests', Math.min(50, formData.max_guests + 1))}
                  >
                    +
                  </Button>
                </div>
              </div>
              <div>
                <Label>Duration</Label>
                <Select
                  value={formData.duration}
                  onValueChange={(value) => handleInputChange('duration', value)}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select duration" />
                  </SelectTrigger>
                  <SelectContent>
                    {['1 hour', '1.5 hours', '2 hours', '2.5 hours', '3 hours', '4 hours', '5 hours', '6+ hours'].map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </section>

          {/* Availability Calendar */}
          <section className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-[#D65A31]" />
              Availability
            </h2>
            <p className="text-gray-500 text-sm mb-6">
              Set when your experience is available for booking
            </p>
            
            {/* Tabs */}
            <div className="flex gap-2 mb-6">
              <Button
                type="button"
                variant={availabilityTab === 'specific' ? 'default' : 'outline'}
                onClick={() => setAvailabilityTab('specific')}
                className={availabilityTab === 'specific' ? 'bg-[#D65A31] hover:bg-[#B84A28]' : ''}
                data-testid="specific-dates-tab"
              >
                <CalendarDays className="w-4 h-4 mr-2" />
                Specific Dates
              </Button>
              <Button
                type="button"
                variant={availabilityTab === 'recurring' ? 'default' : 'outline'}
                onClick={() => setAvailabilityTab('recurring')}
                className={availabilityTab === 'recurring' ? 'bg-[#D65A31] hover:bg-[#B84A28]' : ''}
                data-testid="recurring-tab"
              >
                <Repeat className="w-4 h-4 mr-2" />
                Recurring Weekly
              </Button>
            </div>

            {/* Specific Dates Tab */}
            {availabilityTab === 'specific' && (
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Calendar */}
                  <div>
                    <Label className="mb-2 block">Select a date</Label>
                    <div className="border rounded-lg p-2 inline-block">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        disabled={(date) => date < new Date()}
                        data-testid="availability-calendar"
                      />
                    </div>
                  </div>
                  
                  {/* Time Selection */}
                  <div className="space-y-4">
                    <div>
                      <Label>Time Slot</Label>
                      <div className="grid grid-cols-2 gap-4 mt-2">
                        <div>
                          <Label className="text-sm text-gray-500">Start Time</Label>
                          <Select value={specificStartTime} onValueChange={setSpecificStartTime}>
                            <SelectTrigger data-testid="specific-start-time">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {timeSlots.map((t) => (
                                <SelectItem key={t} value={t}>{formatTime(t)}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-sm text-gray-500">End Time</Label>
                          <Select value={specificEndTime} onValueChange={setSpecificEndTime}>
                            <SelectTrigger data-testid="specific-end-time">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {timeSlots.map((t) => (
                                <SelectItem key={t} value={t}>{formatTime(t)}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                    
                    <Button
                      type="button"
                      onClick={addSpecificDate}
                      disabled={!selectedDate}
                      className="w-full bg-[#D65A31] hover:bg-[#B84A28] text-white"
                      data-testid="add-date-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Date
                    </Button>
                    
                    {selectedDate && (
                      <p className="text-sm text-gray-600 text-center">
                        Selected: {formatDate(selectedDate)} at {formatTime(specificStartTime)} - {formatTime(specificEndTime)}
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Added Dates List */}
                {(formData.availability?.specific_dates?.length > 0) && (
                  <div>
                    <Label className="mb-3 block">Scheduled Dates ({formData.availability.specific_dates.length})</Label>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {formData.availability.specific_dates.map((slot, index) => (
                        <div
                          key={slot.date}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                          data-testid={`date-slot-${index}`}
                        >
                          <div className="flex items-center gap-3">
                            <CalendarDays className="w-4 h-4 text-[#D65A31]" />
                            <span className="font-medium">{formatDate(slot.date)}</span>
                            <span className="text-gray-500">
                              {formatTime(slot.start_time)} - {formatTime(slot.end_time)}
                            </span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeSpecificDate(slot.date)}
                            className="text-red-500 hover:text-red-700 hover:bg-[#FDF8F4]"
                            data-testid={`remove-date-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Recurring Weekly Tab */}
            {availabilityTab === 'recurring' && (
              <div className="space-y-6">
                <div>
                  <Label className="mb-3 block">Select Days</Label>
                  <div className="flex flex-wrap gap-2">
                    {daysOfWeek.map((day) => (
                      <Button
                        key={day}
                        type="button"
                        variant={recurringDays.includes(day) ? 'default' : 'outline'}
                        onClick={() => toggleRecurringDay(day)}
                        className={recurringDays.includes(day) ? 'bg-[#D65A31] hover:bg-[#B84A28]' : ''}
                        data-testid={`day-${day.toLowerCase()}`}
                      >
                        {day.slice(0, 3)}
                      </Button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <Label>Time Slot</Label>
                  <div className="grid grid-cols-2 gap-4 mt-2 max-w-md">
                    <div>
                      <Label className="text-sm text-gray-500">Start Time</Label>
                      <Select value={recurringStartTime} onValueChange={setRecurringStartTime}>
                        <SelectTrigger data-testid="recurring-start-time">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {timeSlots.map((t) => (
                            <SelectItem key={t} value={t}>{formatTime(t)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm text-gray-500">End Time</Label>
                      <Select value={recurringEndTime} onValueChange={setRecurringEndTime}>
                        <SelectTrigger data-testid="recurring-end-time">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {timeSlots.map((t) => (
                            <SelectItem key={t} value={t}>{formatTime(t)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <Button
                    type="button"
                    onClick={saveRecurringSchedule}
                    disabled={recurringDays.length === 0}
                    className="bg-[#D65A31] hover:bg-[#B84A28] text-white"
                    data-testid="save-recurring-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save Schedule
                  </Button>
                  {formData.availability.recurring && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={clearRecurringSchedule}
                      className="text-red-500 border-red-200 hover:bg-[#FDF8F4]"
                      data-testid="clear-recurring-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Clear
                    </Button>
                  )}
                </div>
                
                {/* Current Schedule Display */}
                {formData.availability.recurring && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 text-green-700 font-medium mb-2">
                      <Repeat className="w-4 h-4" />
                      Recurring Schedule Active
                    </div>
                    <p className="text-green-600">
                      Every <strong>{formData.availability.recurring.days.join(', ')}</strong> from{' '}
                      <strong>{formatTime(formData.availability.recurring.start_time)}</strong> to{' '}
                      <strong>{formatTime(formData.availability.recurring.end_time)}</strong>
                    </p>
                  </div>
                )}
              </div>
            )}
          </section>

          {/* Photos & Video */}
          <section className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold mb-2">Photos & Video</h2>
            <p className="text-gray-500 text-sm mb-6">
              <span className="text-red-500 font-medium">Exactly 5 photos required</span> and 1 video (optional, max 200MB)
            </p>
            
            {/* Upload Area */}
            <div
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors mb-6 ${
                dragActive ? 'border-[#D65A31] bg-[#FDF8F4]' : 'border-gray-300'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              data-testid="media-drop-zone"
            >
              {uploadingMedia ? (
                <div className="flex flex-col items-center justify-center gap-2">
                  <Loader2 className="w-8 h-8 animate-spin text-[#D65A31]" />
                  <span className="font-medium">Uploading... {uploadProgress}%</span>
                  <div className="w-48 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-[#D65A31] transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-center gap-4 mb-3">
                    <ImagePlus className="w-8 h-8 text-gray-400" />
                    <Video className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="font-medium mb-1">Drag photos or video here, or click to upload</p>
                  <p className={`text-sm mb-4 ${formData.images.length < 5 ? 'text-red-500 font-medium' : 'text-gray-500'}`}>
                    {formData.images.length}/5 photos {formData.images.length < 5 && `(${5 - formData.images.length} more required)`} · {formData.video ? '1/1' : '0/1'} video
                  </p>
                  <label className="inline-block">
                    <input
                      type="file"
                      accept="image/*,video/mp4,video/quicktime,video/webm"
                      multiple
                      onChange={(e) => handleFiles(e.target.files)}
                      className="hidden"
                      disabled={formData.images.length >= 5 && formData.video}
                      data-testid="media-input"
                    />
                    <span className={`px-4 py-2 rounded-lg cursor-pointer transition-colors ${
                      formData.images.length >= 5 && formData.video
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-[#D65A31] text-white hover:bg-[#B84A28]'
                    }`}>
                      Select Files
                    </span>
                  </label>
                </>
              )}
            </div>

            {/* Video Preview */}
            {formData.video && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <Video className="w-4 h-4" /> Video
                </h3>
                <div className="relative w-64 aspect-video rounded-lg overflow-hidden bg-black group">
                  <video 
                    src={formData.video} 
                    className="w-full h-full object-cover"
                    controls
                  />
                  <button
                    type="button"
                    onClick={removeVideo}
                    className="absolute top-2 right-2 bg-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Image Preview */}
            {formData.images.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <ImagePlus className="w-4 h-4" /> Photos
                </h3>
                <div className="grid grid-cols-5 gap-4">
                  {formData.images.map((url, index) => (
                    <div key={index} className="relative aspect-square rounded-lg overflow-hidden group">
                      <img src={url} alt={`Photo ${index + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-2 right-2 bg-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      {index === 0 && (
                        <span className="absolute bottom-2 left-2 bg-white px-2 py-0.5 rounded text-xs font-medium">
                          Cover
                        </span>
                      )}
                    </div>
                  ))}
                  {formData.images.length < 5 && (
                    <label className="aspect-square border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[#D65A31] transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFiles(e.target.files)}
                        className="hidden"
                      />
                      <Plus className="w-6 h-6 text-gray-400" />
                      <span className="text-xs text-gray-500 mt-1">Add</span>
                    </label>
                  )}
                </div>
              </div>
            )}
          </section>

          {/* What's Included */}
          <section className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold mb-6">What's Included</h2>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {amenitiesList.map((amenity) => (
                <label
                  key={amenity}
                  className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    formData.amenities.includes(amenity)
                      ? 'border-[#D65A31] bg-[#FDF8F4]'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Checkbox
                    checked={formData.amenities.includes(amenity)}
                    onCheckedChange={() => toggleAmenity(amenity)}
                  />
                  <span className="text-sm">{amenity}</span>
                </label>
              ))}
            </div>
          </section>

          {/* Status */}
          {isEditing && (
            <section className="bg-white rounded-xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold mb-4">Status</h2>
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={formData.is_active}
                  onCheckedChange={(checked) => handleInputChange('is_active', checked)}
                />
                <span>Active (visible to guests)</span>
              </label>
            </section>
          )}

          {/* Submit Button (Mobile) */}
          <div className="md:hidden">
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#D65A31] hover:bg-[#B84A28] text-white py-6"
            >
              {loading ? 'Saving...' : (isEditing ? 'Update Experience' : 'Create Experience')}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default CreateEditExperiencePage;
